
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProjectForm } from "@/components/projects/ProjectForm";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";

interface LeadToProjectDialogProps {
  lead: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const LeadToProjectDialog = ({ 
  lead, 
  open, 
  onOpenChange,
  onSuccess 
}: LeadToProjectDialogProps) => {
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  const handleSuccess = async () => {
    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }

    try {
      // Update lead as converted
      const { error: updateError } = await supabase
        .from('leads')
        .update({ 
          converted: true,
          converted_at: new Date().toISOString()
        })
        .eq('id', lead.id)
        .eq('organization_id', currentOrganization.id);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Lead converted to project successfully",
      });

      onSuccess?.();
    } catch (error) {
      console.error('Error converting lead:', error);
      toast({
        title: "Error",
        description: "Failed to convert lead",
        variant: "destructive",
      });
    }
  };

  // Pre-populate project form data from lead with correct status type
  const initialData = {
    name: lead?.name,
    contact_ids: lead?.contact_id ? [lead.contact_id] : [],
    pricing_type: lead?.pricing_type || 'flat',
    price: lead?.price || 0,
    payment_date: lead?.payment_date || '',
    recurring_start_date: lead?.recurring_start_date || '',
    recurring_end_date: lead?.recurring_end_date || '',
    status: "Not Started" as const, // Explicitly type the status as a literal
    organization_id: lead?.organization_id || currentOrganization?.id
  };

  if (!currentOrganization) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Convert Lead to Project</DialogTitle>
        </DialogHeader>
        <ProjectForm 
          initialData={initialData}
          onSuccess={handleSuccess}
        />
      </DialogContent>
    </Dialog>
  );
};
